import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import emailjs from "@emailjs/browser";

import { getPropertyListData, getAgentsData } from "../Constants/Data";
import Button from "../Components/Button";
import PageBanner from "../Components/PageBanner";

const createSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");
};

const displayValue = (value) => {
  return value === 0 || value === "0" ? "N/A" : value;
};

const getYouTubeVideoId = (url) => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?#]+)/,
    /youtube\.com\/watch\?.*v=([^&]+)/,
    /youtu\.be\/([^?]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
};

export default function PropertyDetails() {
  const { slug } = useParams();
  const location = useLocation();
  const propertyIdFromState = location.state?.propertyId;

  const [property, setProperty] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const [agentData, setAgentData] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: "",
    selected_options: "",
  });
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const formRef = useRef(null);

  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  const EMAILJS_CONFIG = {
    serviceId: "service_qcl0p89",
    templateId: "template_hcz55qf",
    publicKey: "7ilGZ_oY1u8taCJvv",
  };

  useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.publicKey);

    async function fetchData() {
      try {
        const allProperties = await getPropertyListData();

        let foundProperty = null;

        if (propertyIdFromState) {
          foundProperty = allProperties.find(
            (prop) => String(prop.pid) === String(propertyIdFromState),
          );
        }

        if (!foundProperty && slug) {
          foundProperty = allProperties.find(
            (prop) => createSlug(prop.ptitle) === slug,
          );
        }

        setProperty(foundProperty || null);

        if (foundProperty?.images?.length) {
          setMainImage(foundProperty.images[0]?.path || "");
          setImages(foundProperty.images.map((img) => img.path));
        } else {
          setMainImage("");
          setImages([]);
        }

        if (foundProperty?.videos?.length) {
          setVideos(foundProperty.videos);
        } else {
          setVideos([]);
        }

        if (foundProperty?.uid) {
          const allAgents = await getAgentsData();
          const foundAgent = allAgents.find(
            (a) => a.agentId === foundProperty.uid,
          );
          setAgentData(foundAgent || {});
        } else {
          setAgentData({});
        }

        if (foundProperty) {
          setFormData((prev) => ({
            ...prev,
            service: `Enquiry about ${foundProperty.ptitle}`,
            selected_options: `Property Type: ${
              foundProperty.ptype
            }\nProperty Status: ${foundProperty.pstatus}\nPrice Guide: ${
              foundProperty.priceguide === 0 || foundProperty.priceguide === "0"
                ? "Contact the seller"
                : foundProperty.priceguide
            }`,
          }));
        }
      } catch (error) {
        console.error("ERROR in fetchData:", error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [slug, propertyIdFromState]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    };
  }, []);

  const slideLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft -= 200;
    }
  };

  const slideRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft += 200;
    }
  };

  const showNextImage = () => {
    const currentIndex = images.indexOf(mainImage);
    if (currentIndex < images.length - 1) {
      setMainImage(images[currentIndex + 1]);
    }
  };

  const showPrevImage = () => {
    const currentIndex = images.indexOf(mainImage);
    if (currentIndex > 0) {
      setMainImage(images[currentIndex - 1]);
    }
  };

  const openImageModal = () => {
    if (mainImage) {
      setShowImageModal(true);
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    }
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    document.body.style.overflow = "auto";
    document.body.style.height = "auto";
  };

  const openVideoModal = (index) => {
    setCurrentVideoIndex(index);
    setCurrentVideoUrl(videos[index]?.url || "");
    setShowVideoModal(true);
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setCurrentVideoUrl("");
    document.body.style.overflow = "auto";
    document.body.style.height = "auto";
  };

  const playNextVideo = () => {
    if (currentVideoIndex < videos.length - 1) {
      const nextIndex = currentVideoIndex + 1;
      setCurrentVideoIndex(nextIndex);
      setCurrentVideoUrl(videos[nextIndex]?.url || "");
    }
  };

  const playPreviousVideo = () => {
    if (currentVideoIndex > 0) {
      const prevIndex = currentVideoIndex - 1;
      setCurrentVideoIndex(prevIndex);
      setCurrentVideoUrl(videos[prevIndex]?.url || "");
    }
  };

  const agentImage =
    agentData?.profileimg && agentData.profileimg !== ""
      ? agentData.profileimg
      : "/profile.jpg";

  const agentName =
    agentData?.firstname && agentData?.lastname
      ? `${agentData.firstname} ${agentData.lastname}`
      : "Umesh Prakash";

  const agentRole =
    agentData?.designation && agentData.designation.trim() !== ""
      ? agentData.designation
      : "Property Manager";

  const socialLinks = agentData?.social_media || {};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");

    if (!formData.name || !formData.email || !formData.message) {
      setSuccessMessage(
        "Please fill in all required fields (Name, Email, and Message)",
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSuccessMessage("Please enter a valid email address");
      return;
    }

    setSending(true);

    try {
      const now = new Date();
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || "Not provided",
        service:
          formData.service || `Enquiry about ${property?.ptitle || "Property"}`,
        message: formData.message,
        selected_options: formData.selected_options || "Property enquiry",
        date: now.toLocaleDateString(),
        time: now.toLocaleTimeString(),
        current_year: now.getFullYear(),
        property_title: property?.ptitle || "Property",
        property_id: property?.pid || "N/A",
        property_address: property?.address
          ? `${property.address.street_address}, ${property.address.suburb}, ${property.address.state}`
          : "Address not available",
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        templateParams,
      );

      if (response.status === 200) {
        setSuccessMessage(
          "Thank you for your enquiry! Our team will contact you shortly.",
        );

        setTimeout(() => {
          setSuccessMessage("");
        }, 5000);

        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
          service: formData.service,
          selected_options: formData.selected_options,
        });
      }
    } catch (error) {
      setSuccessMessage(
        "There was an error sending your message. Please try again later.",
      );

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    } finally {
      setSending(false);
    }
  };

  const pageTitle = property?.ptitle || "Property Details";
  const isRent = (property?.preason || "").toLowerCase().includes("rent");

  return (
    <>
      <Helmet>
        <title>{pageTitle} | Urban View Real Estate PVT LTD</title>
        <meta
          name="description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="keywords"
          content="Affordable Homes for Sale in South Australia, Affordable Homes for Sale in Melbourne, Affordable Homes for Sale in Queensland, Development Sites for Sale in Sydney, Cheap Rent a Home in Penrith, House Buying in Western Australia, Cheap Duplex Home in Tamworth, Affordable duplex Home in Armidale, Houses for Rental in St Marys, Investement Property in Queensland, Best Investment Property Sale in Brisbane, Best Investment Property Sale in Rouse Hill, Box Hill, Marsden Park, Vineyard, Investment Property Buy in Austral, Investment Property Buy in Tallawong, Investment Property Buy in Oran Park, Investment Property Buy in Leppington"
        />
        <meta name="author" content="Urban View Real Estate PVT LTD" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://urbanviewre.com/${pageTitle}`} />

        <meta property="og:title" content="Urban View Real Estate PVT LTD" />
        <meta
          property="og:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta property="og:site_name" content="Urban View Real Estate" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urbanviewre.com" />
        <meta
          property="og:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="twitter:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="twitter:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />
        <meta name="twitter:site" content="@urbanviewre" />
        <meta name="twitter:creator" content="@urbanviewre" />

        <meta
          property="fb:admins"
          content="https://www.facebook.com/profile.php?id=61559035348971"
        />

        <meta name="instagram:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="instagram:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta name="instagram:site" content="@urbanviewrealestate" />
      </Helmet>

      <PageBanner
        title="Property"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house-door-fill" },
          { label: "Properties", href: "/properties" },
          { label: pageTitle, active: true },
        ]}
      />

      <section className="py-5 mb-5 min-vh-100">
        <div className="container py-5">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "300px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !property ? (
            <div className="text-center py-5">
              <p className="text-danger fs-5">
                Sorry, we couldn't find this property.
              </p>
              <p className="text-muted">
                Debug info: Slug used: {slug}, Property ID from state:{" "}
                {propertyIdFromState}
              </p>
            </div>
          ) : (
            <div className="row g-4">
              <div className="col-12 col-xl-9">
                <div className="pb-5 border-bottom">
                  <div
                    className="text-center position-relative w-100"
                    style={{ height: "clamp(260px, 45vw, 400px)" }}
                  >
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt="Main Property Image"
                        className="border"
                        onClick={openImageModal}
                        style={{
                          objectFit: "cover",
                          height: "100%",
                          width: "100%",
                          cursor: "pointer",
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center bg-light text-muted"
                        style={{
                          height: "clamp(260px, 45vw, 400px)",
                          width: "100%",
                        }}
                      >
                        No Image Available
                      </div>
                    )}

                    <span
                      className={`position-absolute top-0 end-0 m-3 text-white fw-bold px-3 rounded-2 py-2 fs-6 shadow-sm ${
                        property.pstatus === "Available"
                          ? "bg-success"
                          : property.pstatus === "Leased"
                            ? "bg-warning"
                            : property.pstatus === "Sell"
                              ? "bg-danger"
                              : "bg-secondary"
                      }`}
                    >
                      {property.pstatus === "Sell" ? "Sold" : property.pstatus}
                    </span>
                  </div>

                  {images.length > 0 ? (
                    <div className="position-relative mt-3">
                      {images.length > 4 && (
                        <button
                          className="btn btn-dark position-absolute start-0 top-50 translate-middle-y"
                          onClick={slideLeft}
                          style={{ zIndex: 10 }}
                        >
                          ❮
                        </button>
                      )}

                      <div
                        ref={sliderRef}
                        className="d-flex align-items-center gap-2 overflow-auto py-2"
                        style={{
                          scrollBehavior: "smooth",
                          whiteSpace: "nowrap",
                          scrollbarWidth: "thin",
                        }}
                      >
                        {images.map((img, index) => (
                          <div
                            key={index}
                            className="p-1"
                            onClick={() => setMainImage(img)}
                            style={{ cursor: "pointer", flexShrink: 0 }}
                          >
                            <img
                              src={img}
                              alt={`Thumbnail ${index}`}
                              className="img-fluid rounded border shadow-sm"
                              style={{
                                border:
                                  mainImage === img ? "3px solid blue" : "none",
                                objectFit: "cover",
                                width: "120px",
                                height: "80px",
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      {images.length > 4 && (
                        <button
                          className="btn btn-dark position-absolute end-0 top-50 translate-middle-y"
                          onClick={slideRight}
                          style={{ zIndex: 10 }}
                        >
                          ❯
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="mt-3 mb-0">No images available</p>
                  )}
                </div>

                <h1 className="fw-bold py-3 m-0">{property.ptitle}</h1>

                <span className="small d-inline-block">
                  <i className="bi bi-geo-alt-fill text-primary"></i>{" "}
                  {property.address?.street_address}, {property.address?.suburb},{" "}
                  {property.address?.state}
                </span>

                <h6 className="border-primary border-3 border-start p-2 mt-3">
                  Description
                </h6>
                <div
                  className="py-3"
                  dangerouslySetInnerHTML={{
                    __html: property?.description || "",
                  }}
                />

                <h6 className="border-primary border-3 border-start p-2 mb-3">
                  Property Detail
                </h6>

                <div className="row bg-light g-0 rounded overflow-hidden">
                  <div className="col-12 col-md-6 col-lg-4 p-4 p-lg-5 border-end">
                    <p>Property Area: {displayValue(property.land_size)} sqm</p>
                    <p>Rooms: {displayValue(property.rooms)}</p>
                    <p>Baths: {displayValue(property.baths)}</p>
                    <p>Year Built: {displayValue(property.built_year)}</p>
                    <p>Property Type: {property.ptype}</p>
                  </div>

                  <div className="col-12 col-md-6 col-lg-8 p-4 p-lg-5">
                    <p>Reason: {property.preason}</p>
                    <p>Beds: {displayValue(property.beds)}</p>
                    <p>
                      {isRent ? "Rent" : "Price"}:{" "}
                      {property.priceguide === 0 || property.priceguide === "0"
                        ? "Contact the seller"
                        : isRent
                          ? `$${property.priceguide}/w`
                          : property.priceguide}
                    </p>
                    <p>Property Status: {property.pstatus}</p>
                  </div>
                </div>

                {videos.length > 0 && (
                  <div className="mt-4">
                    <h6 className="border-primary border-3 border-start p-2 mb-3">
                      Property Videos
                    </h6>

                    <div className="d-flex flex-wrap gap-3">
                      {videos.map((video, index) => {
                        const videoId = getYouTubeVideoId(video.url);

                        return (
                          <div
                            key={index}
                            className="position-relative"
                            style={{ width: "200px", cursor: "pointer" }}
                            onClick={() => openVideoModal(index)}
                          >
                            {videoId ? (
                              <>
                                <img
                                  src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                  alt={`Video thumbnail ${index + 1}`}
                                  className="img-fluid rounded shadow-sm"
                                  style={{
                                    width: "100%",
                                    height: "120px",
                                    objectFit: "cover",
                                  }}
                                />
                                <div
                                  className="position-absolute d-flex justify-content-center align-items-center top-50 start-50 translate-middle text-white bg-dark bg-opacity-75 rounded-circle"
                                  style={{ width: "55px", height: "55px" }}
                                >
                                  <svg
                                    width="35"
                                    height="35"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </div>
                              </>
                            ) : (
                              <div
                                className="bg-light rounded shadow-sm d-flex align-items-center justify-content-center"
                                style={{ width: "200px", height: "120px" }}
                              >
                                <i className="bi bi-camera-reels-fill fs-1 text-muted"></i>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="col-12 col-xl-3">
                <div className="row g-4">
                  <div className="col-12 col-md-6 col-xl-12 order-1">
                    <div className="card text-center shadow-sm rounded-0 h-100">
                      <img
                        src={agentImage}
                        alt={agentName}
                        className="card-img-top object-fit-cover"
                        style={{
                          height: "260px",
                          objectFit: "cover",
                        }}
                      />

                      <div className="card-body d-flex flex-column justify-content-center">
                        <h5 className="fw-bold mb-1">{agentName}</h5>
                        <p className="text-primary fw-semibold mb-3">
                          {agentRole}
                        </p>

                        <div className="d-flex justify-content-center gap-3">
                          {socialLinks.facebook && (
                            <a
                              href={socialLinks.facebook}
                              className="text-dark fs-5"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-facebook"></i>
                            </a>
                          )}

                          {socialLinks.twitter && (
                            <a
                              href={socialLinks.twitter}
                              className="text-dark fs-5"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-twitter"></i>
                            </a>
                          )}

                          {socialLinks.linkedin && (
                            <a
                              href={socialLinks.linkedin}
                              className="text-dark fs-5"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-linkedin"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-xl-12 order-2">
                    <div className="card shadow-sm rounded-0 h-100">
                      <div className="card-body p-3 p-md-4">
                        <h5 className="fw-bold mb-3 border-start border-4 border-primary ps-3">
                          Drop Message For Book
                        </h5>

                        <form ref={formRef} onSubmit={handleSubmit}>
                          <div className="mb-3">
                            <input
                              type="text"
                              name="name"
                              className="form-control rounded-0"
                              placeholder="Your Name *"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <input
                              type="email"
                              name="email"
                              className="form-control rounded-0"
                              placeholder="Your Email *"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <input
                              type="tel"
                              name="phone"
                              className="form-control rounded-0"
                              placeholder="Phone Number"
                              value={formData.phone}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="mb-3">
                            <textarea
                              name="message"
                              className="form-control rounded-0"
                              placeholder="Write Message... *"
                              rows="4"
                              value={formData.message}
                              onChange={handleInputChange}
                              required
                            ></textarea>
                          </div>

                          <Button
                            text="Submit Message"
                            type="submit"
                            loading={sending}
                            className="text-muted w-100"
                          />

                          {successMessage && (
                            <div
                              className={`alert small ${
                                successMessage.includes("Thank you")
                                  ? "alert-success"
                                  : "alert-danger"
                              } mt-3 mb-0`}
                              role="alert"
                            >
                              {successMessage}
                            </div>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {showImageModal && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: "rgba(0,0,0,0.8)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            overflowY: "auto",
          }}
          onClick={closeImageModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            style={{ maxWidth: "1100px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-transparent border-0">
              <div className="modal-body p-0 position-relative text-center">
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    maxWidth: "100%",
                    maxHeight: "90vh",
                    background: "#000",
                    cursor: "zoom-in",
                  }}
                >
                  <img
                    src={mainImage}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "90vh",
                      objectFit: "contain",
                      display: "block",
                      userSelect: "none",
                      cursor: "zoom-in",
                    }}
                  />

                  {images.length > 1 && (
                    <button
                      className="btn btn-light position-absolute start-0 top-50 translate-middle-y ms-3 rounded-circle"
                      onClick={showPrevImage}
                      style={{
                        width: "50px",
                        height: "50px",
                        opacity: images.indexOf(mainImage) === 0 ? 0.5 : 1,
                        cursor:
                          images.indexOf(mainImage) === 0
                            ? "not-allowed"
                            : "pointer",
                        zIndex: 10,
                      }}
                      disabled={images.indexOf(mainImage) === 0}
                    >
                      <i className="bi bi-chevron-left fs-4"></i>
                    </button>
                  )}

                  {images.length > 1 && (
                    <button
                      className="btn btn-light position-absolute end-0 top-50 translate-middle-y me-3 rounded-circle"
                      onClick={showNextImage}
                      style={{
                        width: "50px",
                        height: "50px",
                        opacity:
                          images.indexOf(mainImage) === images.length - 1
                            ? 0.5
                            : 1,
                        cursor:
                          images.indexOf(mainImage) === images.length - 1
                            ? "not-allowed"
                            : "pointer",
                        zIndex: 10,
                      }}
                      disabled={images.indexOf(mainImage) === images.length - 1}
                    >
                      <i className="bi bi-chevron-right fs-4"></i>
                    </button>
                  )}
                </div>

                <button
                  className="btn btn-light position-absolute top-0 end-0 m-3 rounded-circle"
                  onClick={closeImageModal}
                  style={{
                    width: "45px",
                    height: "45px",
                    fontSize: "20px",
                    fontWeight: "bold",
                    zIndex: 10,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVideoModal && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: "rgba(0,0,0,0.8)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            overflowY: "auto",
          }}
          onClick={closeVideoModal}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            style={{ maxWidth: "900px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0"></div>

              <div className="modal-body p-0">
                <div className="position-relative">
                  <div className="ratio ratio-16x9">
                    {currentVideoUrl && (
                      <iframe
                        src={`https://www.youtube.com/embed/${getYouTubeVideoId(currentVideoUrl)}?autoplay=1`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>

                  {videos.length > 1 && (
                    <>
                      <button
                        className="btn btn-light position-absolute start-0 top-50 translate-middle-y ms-3 rounded-circle"
                        onClick={playPreviousVideo}
                        style={{
                          width: "50px",
                          height: "50px",
                          opacity: currentVideoIndex === 0 ? 0.5 : 1,
                          cursor:
                            currentVideoIndex === 0 ? "not-allowed" : "pointer",
                        }}
                        disabled={currentVideoIndex === 0}
                      >
                        <i className="bi bi-chevron-left fs-4"></i>
                      </button>

                      <button
                        className="btn btn-light position-absolute end-0 top-50 translate-middle-y me-3 rounded-circle"
                        onClick={playNextVideo}
                        style={{
                          width: "50px",
                          height: "50px",
                          opacity:
                            currentVideoIndex === videos.length - 1 ? 0.5 : 1,
                          cursor:
                            currentVideoIndex === videos.length - 1
                              ? "not-allowed"
                              : "pointer",
                        }}
                        disabled={currentVideoIndex === videos.length - 1}
                      >
                        <i className="bi bi-chevron-right fs-4"></i>
                      </button>
                    </>
                  )}

                  {videos.length > 1 && (
                    <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3 bg-dark bg-opacity-75 text-white px-3 py-1 rounded-pill">
                      {currentVideoIndex + 1} / {videos.length}
                    </div>
                  )}
                </div>

                {videos.length > 1 && (
                  <div
                    className="mt-3 d-flex justify-content-center gap-2 overflow-auto py-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {videos.map((video, index) => {
                      const videoId = getYouTubeVideoId(video.url);

                      return (
                        <div
                          key={index}
                          onClick={() => {
                            setCurrentVideoIndex(index);
                            setCurrentVideoUrl(video.url);
                          }}
                          style={{
                            width: "120px",
                            height: "70px",
                            cursor: "pointer",
                            border:
                              index === currentVideoIndex
                                ? "3px solid #0d6efd"
                                : "3px solid transparent",
                            borderRadius: "4px",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          {videoId ? (
                            <img
                              src={`https://img.youtube.com/vi/${videoId}/default.jpg`}
                              alt={`Thumbnail ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              className="bg-secondary d-flex align-items-center justify-content-center text-white"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <small>Video {index + 1}</small>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}